<script lang="ts">
  import type { View } from '../lib/types';
  import { coarsePointer } from '../lib/stores/viewport';

  interface Props {
    view: View;
    visible: boolean;
    /** Where to position relative to the parent element */
    position?: 'below' | 'left';
  }

  let { view, visible, position = 'below' }: Props = $props();

  const descriptions: Record<View, { title: string; description: string }> = {
    focus: {
      title: 'Focus View',
      description:
        'Full kanban board for the current period. See all projects organized by status — Planned, Active, Almost Done, and Done.',
    },
    rolling: {
      title: 'Rolling View',
      description:
        "Scrolling timeline of months or weeks with project cards. Great for seeing what's coming up across multiple periods.",
    },
    days: {
      title: 'Days View',
      description:
        'Day-by-day grid showing exactly when projects are scheduled. Best for detailed daily planning and seeing overlaps.',
    },
    table: {
      title: 'Table View',
      description:
        'Compact list of projects grouped by time period. Quick overview with status at a glance.',
    },
    timeline: {
      title: 'Timeline View',
      description:
        'Horizontal bars showing project duration across time. See overlaps and gaps in your schedule at a glance.',
    },
    gantt: {
      title: 'Gantt Chart',
      description:
        'Project timelines on a continuous date axis. Traditional project management view for long-range planning.',
    },
    'grid-6x2': {
      title: 'Year Grid',
      description:
        'Full year at a glance in a 6×2 grid layout. Quick annual overview showing project distribution across months.',
    },
    'grid-quarters': {
      title: 'Quarters View',
      description:
        'Year organized by Q1–Q4 with month columns within each quarter. See projects grouped by quarter.',
    },
    tasks: {
      title: 'Task Board',
      description:
        'Kanban board for individual tasks across all projects. Drag tasks between To Do, Working On, and Done lanes.',
    },
  };

  let info = $derived(descriptions[view]);
</script>

<!-- Hover-only: never on touch, where it would open on a tap and get stuck. -->
{#if visible && info && !$coarsePointer}
  <div class="view-tooltip {position}" role="tooltip">
    <div class="tooltip-title">{info.title}</div>
    <div class="tooltip-desc">{info.description}</div>
  </div>
{/if}

<style>
  .view-tooltip {
    position: absolute;
    width: 14rem;
    padding: 0.5rem 0.625rem;
    background: #1f2937;
    color: white;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
    pointer-events: none;
  }

  /* Position: below (default, used for navbar buttons) */
  .view-tooltip.below {
    top: calc(100% + 0.5rem);
    left: 50%;
    transform: translateX(-50%);
    animation: fadeInBelow 0.15s ease;
  }

  /* Position: left (used for dropdown menu items) */
  .view-tooltip.left {
    right: calc(100% + 0.5rem);
    top: 50%;
    transform: translateY(-50%);
    animation: fadeInLeft 0.15s ease;
  }

  .tooltip-title {
    font-size: 0.6875rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .tooltip-desc {
    font-size: 0.625rem;
    line-height: 1.4;
    color: #d1d5db;
  }

  @keyframes fadeInBelow {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translateY(-50%) translateX(4px);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }
</style>
